import { useState } from 'react';
import { Sheet } from '../shared/Sheet';
import { Icon } from '../chrome/Icon';
import { useT } from '../../i18n';
import { useApiKeys, useCreateApiKey, useDeleteApiKey } from '../../data/queries';
import type { ApiKeyItem } from '../../data/dto';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ApiKeySheet({ open, onClose }: Props) {
  const t = useT();
  const keysQuery = useApiKeys(open);
  const createMutation = useCreateApiKey();
  const deleteMutation = useDeleteApiKey();

  const [keyName, setKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const keys = keysQuery.data ?? [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = keyName.trim();
    if (!name) return;

    setErrorMsg(null);
    setCreatedKey(null);
    setCopied(false);

    try {
      const res = await createMutation.mutateAsync({ name });
      setCreatedKey(res.api_key.key);
      setKeyName('');
    } catch (err: any) {
      console.error('Failed to create API key:', err);
      setErrorMsg(err.message || t('profile.apiKeyCreateError', 'Failed to generate API key.'));
    }
  };

  const handleCopy = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleDelete = async (id: string) => {
    if (deletingId) return;
    setDeletingId(id);
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err: any) {
      console.error('Failed to delete API key:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t('profile.apiKeyNeverUsed', 'Never used');
    try {
      const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      size="md"
      maxHeight="92dvh"
      label={t('profile.apiKeysTitle', 'Personal API Keys')}
    >
      <div className="flex flex-col gap-6 px-6 pb-8 pt-0 overflow-y-auto">
        {/* Top bar with title and close button */}
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('profile.developerOptions', 'Developer & Integrations')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center cursor-pointer"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        <div>
          <h1 className="font-serif text-[28px] leading-tight tracking-tight text-ink">
            {t('profile.apiKeysTitle', 'Personal API Keys')}
          </h1>
          <p className="text-[14.5px] leading-relaxed text-ink-2 mt-1">
            {t(
              'profile.apiKeysDesc',
              'Generate persistent API keys for your local Mac Mini dashboard, Home Assistant, or custom home integrations.'
            )}
          </p>
        </div>

        {/* Newly Created Key Alert Box */}
        {createdKey && (
          <div className="rounded-2xl border border-[#2e7d32]/30 bg-[#e8f5e9] p-4 text-ink animate-fade-in">
            <div className="flex items-center gap-2 text-[#1b5e20] font-semibold text-[14px] mb-1">
              <Icon name="check" size={18} color="currentColor" />
              <span>{t('profile.apiKeyCreatedHead', 'API Key Generated!')}</span>
            </div>
            <p className="text-[12.5px] text-[#2e7d32] mb-3">
              {t(
                'profile.apiKeyCopyWarning',
                'Copy your key now. For your security, it will never be displayed again!'
              )}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={createdKey}
                className="w-full rounded-xl border border-[#a5d6a7] bg-white px-3 py-2 text-[13px] font-mono text-ink outline-none select-all"
              />
              <button
                type="button"
                onClick={handleCopy}
                className="shrink-0 inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl bg-[#1b5e20] px-3.5 text-[13px] font-semibold text-white hover:bg-[#1b5e20]/90 transition-colors cursor-pointer"
              >
                <Icon name={copied ? 'check' : 'copy'} size={15} color="currentColor" />
                {copied ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
              </button>
            </div>
          </div>
        )}

        {/* Create API Key Form */}
        <form onSubmit={handleCreate} className="flex flex-col gap-3.5">
          <label
            htmlFor="api-key-name"
            className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3"
          >
            {t('profile.generateNewKey', 'Generate New API Key')}
          </label>
          <div className="flex items-center gap-2">
            <input
              id="api-key-name"
              type="text"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder={t('profile.apiKeyNamePlaceholder', 'e.g. Mac Mini Dashboard')}
              maxLength={100}
              className="w-full rounded-xl border border-line bg-surface px-4 py-2.5 text-[14.5px] text-ink outline-none focus:border-ink transition-colors"
            />
            <button
              type="submit"
              disabled={!keyName.trim() || createMutation.isPending}
              className="shrink-0 inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl bg-ink px-4 text-[13.5px] font-semibold text-bg disabled:opacity-50 hover:bg-ink/90 transition-all cursor-pointer"
            >
              {createMutation.isPending ? (
                <span className="w-4 h-4 border-2 border-bg border-t-transparent animate-spin rounded-full" />
              ) : (
                <>
                  <Icon name="plus" size={16} color="currentColor" />
                  {t('profile.generateBtn', 'Generate')}
                </>
              )}
            </button>
          </div>
          {errorMsg && <p className="text-[12.5px] text-[#B5523B]">{errorMsg}</p>}
        </form>

        {/* Existing Keys List */}
        <div className="flex flex-col gap-3 mt-2">
          <h2 className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('profile.yourApiKeys', 'Active Keys ({count})').replace('{count}', String(keys.length))}
          </h2>

          {keysQuery.isLoading ? (
            <div className="flex justify-center py-6">
              <span className="w-6 h-6 border-2 border-ink border-t-transparent animate-spin rounded-full" />
            </div>
          ) : keys.length === 0 ? (
            <div className="rounded-2xl border border-line/60 bg-grey-soft/40 p-5 text-center text-[13.5px] text-ink-2">
              {t('profile.noApiKeysYet', 'No API keys created yet. Generate one above to access your dashboard.')}
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {keys.map((k: ApiKeyItem) => (
                <div
                  key={k.id}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface p-3.5 text-ink transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[14.5px] truncate">{k.name}</span>
                      <code className="shrink-0 rounded-md bg-grey-soft px-2 py-0.5 font-mono text-[11.5px] text-ink-2">
                        {k.key_prefix}…
                      </code>
                    </div>
                    <div className="text-[12px] text-ink-3 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>Created {formatDate(k.created_at)}</span>
                      <span>•</span>
                      <span>Last used: {formatDate(k.last_used_at)}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleDelete(k.id)}
                    disabled={deletingId === k.id}
                    title={t('common.delete', 'Revoke Key')}
                    aria-label={t('common.delete', 'Revoke Key')}
                    className="flex min-h-9 min-w-9 shrink-0 items-center justify-center rounded-xl text-ink-3 hover:bg-[#B5523B]/10 hover:text-[#B5523B] transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {deletingId === k.id ? (
                      <span className="w-4 h-4 border-2 border-current border-t-transparent animate-spin rounded-full" />
                    ) : (
                      <Icon name="trash" size={17} color="currentColor" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
}
